import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Select, FormControl, InputLabel, MenuItem, FormHelperText, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../config";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";

const AttendanceReport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [disciplines, setDisciplines] = useState([]);

  const validateForm = (values) => {
    if (!values.discipline) {
      toast.error("Please select a discipline");
      return false;
    }
    if (!values.startDate) {
      toast.error("Please select a start date");
      return false;
    }
    if (!values.endDate) {
      toast.error("Please select an end date");
      return false;
    }
    return true;
  };

  const handleShowReport = async (values) => {
    if (!validateForm(values)) return;
    
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/show-attendance-report`,
        values,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Open HTML report in new window
      const newWindow = window.open("", "_blank");
      newWindow.document.write(response.data);
      newWindow.document.close();

      toast.success("Attendance report displayed successfully");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || "Failed to show attendance report");
    }
  };

  const handleGenerateReport = async (values) => {
    if (!validateForm(values)) return;
    
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/generate-attendance-report`,
        values,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "attendance_report.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Attendance report generated successfully");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || "Failed to generate attendance report");
    }
  };

  useEffect(() => {     
    const fetchDisciplines = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/get_disciplines`);
        console.log(response.data);
        setDisciplines(response.data.disciplines);
      } catch (error) {
        console.error("Failed to fetch disciplines:", error);
        toast.error("Failed to fetch disciplines");
      }
    };

    fetchDisciplines();
  }, []);

  const initialValues = {
    discipline: "",
    startDate: "",
    endDate: "",
  };

  const validationSchema = yup.object().shape({
    discipline: yup.string().required("Discipline is required"),
    startDate: yup.date().required("Start date is required"),
    endDate: yup.date().required("End date is required"),
  });

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: { xs: 1.5, sm: 2 } }}>
        Attendance report of members for each discipline
      </Typography>
      <Formik
        onSubmit={() => {}}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue
        }) => (
          <form>
            <Box
              display="grid"
              gap={{ xs: "14px", sm: "18px", md: "20px" }}
              gridTemplateColumns={{ xs: "repeat(1, minmax(0, 1fr))", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }}
            >
              <FormControl fullWidth variant="filled" error={!!touched.disciplines && !!errors.disciplines} sx={{ gridColumn: { xs: "span 1", sm: "span 2", lg: "span 1" } }}>
                <InputLabel id="discipline-label">Discipline</InputLabel>
                <Select
                labelId="discipline-label"
                value={values.discipline}
                onChange={(e) => setFieldValue("discipline", e.target.value)}
                >
                  {disciplines.map((discipline) => (
                      <MenuItem key={discipline.discipline_id} value={discipline.discipline_id}>
                        {discipline.discipline_name}
                      </MenuItem>
                  ))}
                </Select>
                {touched.enrollments && errors.enrollments && (
                  <FormHelperText>{errors.enrollments}</FormHelperText>
                )}
              </FormControl>
              <TextField
                fullWidth
                variant="filled"
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.startDate}
                name="startDate"
                error={!!touched.startDate && !!errors.startDate}
                helperText={touched.startDate && errors.startDate}
                sx={{ gridColumn: { xs: "span 1", sm: "span 1", lg: "span 1" } }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.endDate}
                name="endDate"
                error={!!touched.endDate && !!errors.endDate}
                helperText={touched.endDate && errors.endDate}
                sx={{ gridColumn: { xs: "span 1", sm: "span 1", lg: "span 1" } }}
              />
            </Box>
            <Box display="flex" justifyContent="flex-start" flexWrap="wrap" mt={{ xs: 2, sm: 2.5, md: 3 }} gap={1}>
              <Button 
                onClick={() => handleShowReport(values)} 
                color="primary" 
                variant="contained"
                startIcon={<VisibilityIcon />}
                size={isMobile ? "small" : "medium"}
                sx={{ minWidth: { xs: 0, sm: 150 }, textTransform: "none", fontWeight: 700 }}
              >
                Show Report
              </Button>
              <Button 
                onClick={() => handleGenerateReport(values)} 
                color="secondary" 
                variant="contained"
                startIcon={<DownloadIcon />}
                size={isMobile ? "small" : "medium"}
                sx={{ minWidth: { xs: 0, sm: 170 }, textTransform: "none", fontWeight: 700 }}
              >
                Generate Report
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default AttendanceReport;
